import * as React from "react"
import styled from "styled-components"
import { SpaceProps } from "styled-system"
import { space } from "../../styles/styled-system"
import { scale, colors, bodyFontFamily } from "../../styles"
import { Icon, LayoutProps, Text, Flex } from "../"

export interface SelectOption {
	label: string
	value: string
	regionId?: string
	registrationLink?: string
}

export interface SelectStyledProps
	extends SpaceProps,
		Omit<React.HTMLProps<HTMLSelectElement>, "size" | "width">,
		Omit<LayoutProps, "as"> {
	error?: boolean
}
export interface SelectProps extends SelectStyledProps {
	placeholder?: string
	id?: string
	options: SelectOption[]
	inputRef?: React.Ref<HTMLSelectElement>
	helperText?: React.ReactNode
	label?: string
}

const SelectHolderStyled = styled.div`
	position: relative;
	flex: 1;
`
const IconStyled = styled.span`
	position: absolute;
	top: 50%;
	right: ${scale.px(1.5)};
	transform: translateY(-50%);
	pointer-events: none;
`

const SelectStyled = styled.select<SelectStyledProps>`
	display: block;
	width: 100%;
	position: relative;
	border: 1px solid ${colors.blue33};
	border-color: ${({ error }) => colors[error ? "alert" : "blue33"]};
	color: ${colors.navy};
	height: ${scale.px(6)};
	padding: 0 ${scale.px(2)};
	border-radius: 4px;
	background: ${colors.white};
	font-family: ${bodyFontFamily};
	font-size: ${scale.px(2)};
	line-height: ${scale.px(3)};
	overflow: hidden;
	-webkit-appearance: none;
	-moz-appearance: none;
	text-indent: 1px;

	&::-ms-expand {
		display: none;
	}

	&:disabled {
		opacity: 50%;

		& ~ ${IconStyled} {
			opacity: 50%;
		}
	}

	&:focus {
		outline: 0;
		border-color: ${colors.navy};
	}

	${space};
`

const Label = styled.label`
	pointer-events: none;
	font-size: ${scale.px(1.75)};
	line-height: ${scale.px(2.5)};
	margin-bottom: ${scale.px(0.5)};
`

const Select: React.FC<SelectProps> = (props) => {
	const {
		placeholder,
		options,
		ref,
		as,
		size,
		inputRef,
		helperText,
		label,
		multiple,
		value,
		...rest
	} = props

	return (
		<Flex display="block" width={1}>
			{label && <Label htmlFor={rest.id}>{label}</Label>}
			<SelectHolderStyled>
				<SelectStyled
					ref={inputRef}
					defaultValue={placeholder ? placeholder : options[0].value}
					aria-invalid={rest.error ? "true" : "false"}
					{...rest}
				>
					{placeholder && <option value="">{placeholder}</option>}
					{options.map((option) => (
						<option
							key={`${option.label}-${option.value}-key`}
							value={option.value}
						>
							{option.label}
						</option>
					))}
				</SelectStyled>
				<IconStyled>
					<Icon icon="chevron-down" size={2.5} />
				</IconStyled>
			</SelectHolderStyled>
			<Flex w={1} minh={helperText ? "none" : 3} justifyContent="flex-end">
				<Text
					as="span"
					textColor={rest.error ? "alert" : "navy"}
					fontSize={1.75}
					lineHeight={2.5}
					mt={0.5}
					mb={0}
				>
					{helperText}
				</Text>
			</Flex>
		</Flex>
	)
}

Select.defaultProps = {
	error: false,
}

export { Select }
